// @ts-nocheck
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Chart from 'chart.js/auto';
import type { ChartOptions, ChartEvent, LegendItem, ChartDataset } from 'chart.js';

interface CumulativeFlowDiagramProps {
  title: string;
  xLabel: string;
  yLabel: string;
  labels: string[];
  dataPoints: {
    date: string;
    stateCounts: { [key: string]: number };
    phaseRanks: { [key: string]: number };
  }[];
  colorPalette: { [key: string]: string };
}
export interface CumulativeFlowDiagramRef {
  highlightSuperPhases: () => void;
}

const CumulativeFlowDiagram = forwardRef<CumulativeFlowDiagramRef, CumulativeFlowDiagramProps>(
  ({ title, xLabel, yLabel, labels, dataPoints, colorPalette }, ref) => {
    const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
    const [hiddenPhases, setHiddenPhases] = useState<Set<string>>(new Set());

    const chartRef = useRef<Chart<'line'> | null>(null);

    const createDatasets = (
      dataPoints: any[] = [],
      hiddenPhases: Set<string> = new Set(),
      colorPalette: Record<string, string> = {}
    ) => {
      if (!Array.isArray(dataPoints) || dataPoints.length === 0) {
        return [
          {
            label: 'No Data',
            data: [] as number[],
            fill: false,
            hidden: false,
          },
        ];
      }
      const phaseRanks = dataPoints[0]?.phaseRanks ?? {};
      const sortedPhases = Object.entries(phaseRanks)
        .sort(([, rankA], [, rankB]) => Number(rankA) - Number(rankB))
        .map(([phaseName]) => phaseName);
      const visiblePhases = sortedPhases.filter((phaseName) => !hiddenPhases.has(phaseName));
      const hiddenPhaseEntries = sortedPhases.filter((phaseName) => hiddenPhases.has(phaseName));
      const allPhases = [...visiblePhases, ...hiddenPhaseEntries];

      return allPhases.map((phaseName) => {
        const data = dataPoints.map((point) => {
          const cumulativeValue = Object.entries(point.phaseRanks)
            .filter(
              ([pName, rank]) =>
                Number(rank) <= point.phaseRanks[phaseName] && !hiddenPhases.has(pName)
            )
            .reduce((sum, [pName]) => sum + (point.stateCounts[pName] ?? 0), 0);
          return cumulativeValue;
        });

        const color = colorPalette[phaseName] || 'rgba(0, 0, 0, 1)';
        return {
          label: phaseName,
          data,
          borderColor: color,
          backgroundColor: color.replace('1)', '0.2)'),
          fill: true,
          hidden: hiddenPhases.has(phaseName),
        };
      });
    };

    useEffect(() => {
      if (!canvasEl) return;

      const ctx = canvasEl.getContext('2d');
      const datasets = createDatasets(dataPoints, hiddenPhases, colorPalette);

      const chart = new Chart(ctx!, {
        type: 'line',
        data: {
          labels,
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: title,
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: (tooltipItem) => {
                  const dataset = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex];
                  const label = dataset.label;
                  const index = tooltipItem.dataIndex;
                  const rawValue = dataPoints[index].stateCounts[label!];

                  return `${label}: ${rawValue ?? 0}`;
                },
              },
            },
            legend: {
              onClick: (e: ChartEvent, legendItem: LegendItem) => {
                const chartInstance = chartRef.current;
                if (!chartInstance) return;

                const phaseName = legendItem.text!;
                const newHiddenPhases = new Set(hiddenPhases);
                if (newHiddenPhases.has(phaseName)) {
                  newHiddenPhases.delete(phaseName);
                } else {
                  newHiddenPhases.add(phaseName);
                }
                setHiddenPhases(newHiddenPhases);
              },
            },
          },
          scales: {
            x: {
              type: 'category',
              title: {
                display: true,
                text: xLabel,
              },
            },
            y: {
              title: {
                display: true,
                text: yLabel,
              },
            },
          },
        },
      });

      chartRef.current = chart;
      resizeCanvas();

      return () => {
        chartRef.current?.destroy();
        chartRef.current = null;
      };
    }, [canvasEl, dataPoints, labels, colorPalette, title, xLabel, yLabel, hiddenPhases]);

    const resizeCanvas = () => {
      if (canvasEl) {
        canvasEl.width = window.innerWidth * 0.9;
        canvasEl.height = window.innerHeight * 0.9;
      }
    };

    const highlightSuperPhases = () => {
      setHiddenPhases(new Set());
    };

    useImperativeHandle(ref, () => ({
      highlightSuperPhases,
    }));

    return (
      <canvas
        ref={setCanvasEl}
        style={{
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          boxSizing: 'border-box',
          display: 'block',
        }}
      ></canvas>
    );
  }
);

export default CumulativeFlowDiagram;
